// Oracle CHECK (col IS JSON [STRICT]) detector.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice A3.
//
// Operates on `ALL_CONSTRAINTS.SEARCH_CONDITION` strings (CONSTRAINT_TYPE = 'C').
// Oracle's IS JSON constraint can be either:
//   "col" IS JSON
//   "col" IS JSON STRICT
//   "col" IS JSON (WITH UNIQUE KEYS)
//   col IS JSON
//
// `STRICT` and the option-list form both classify as `check-isjson`; the
// strict-vs-lax distinction is propagated as a `strict?: boolean` flag so
// later phases (Phase C operator suggestion / pathHints style preferences)
// can consume it. The Oracle schema cache currently does NOT expose CHECK
// constraints, so end-to-end wiring lands when oracleSchemaIntrospection
// fetches SEARCH_CONDITION (Phase G4 / a follow-up Oracle constraint slice).

const IS_JSON_CHECK = /(?:"([^"]+)"|([A-Za-z_][\w$#]*))\s+IS\s+JSON(?:\s+STRICT)?(?:\s*\([^)]*\))?/gi;
const STRICT_RE = /\bIS\s+JSON\s+STRICT\b/i;

export type OracleIsJsonHit = { readonly column: string; readonly strict: boolean };

export function detectOracleIsJsonColumns(
  checkClauses: readonly string[] | null | undefined,
): ReadonlyMap<string, OracleIsJsonHit> {
  if (!checkClauses || checkClauses.length === 0) return EMPTY;
  const out = new Map<string, OracleIsJsonHit>();
  for (const clause of checkClauses) {
    if (!clause) continue;
    if (isCompoundCheck(clause)) continue;
    let match: RegExpExecArray | null;
    IS_JSON_CHECK.lastIndex = 0;
    while ((match = IS_JSON_CHECK.exec(clause)) !== null) {
      const quoted = match[1];
      const bare = match[2];
      const name = quoted ?? bare;
      if (!name) continue;
      const strict = STRICT_RE.test(match[0]);
      out.set(name, { column: name, strict });
    }
  }
  return out.size === 0 ? EMPTY : out;
}

function isCompoundCheck(clause: string): boolean {
  const stripped = clause.replace(IS_JSON_CHECK, '');
  return /\b(AND|OR)\b/i.test(stripped);
}

const EMPTY: ReadonlyMap<string, OracleIsJsonHit> = new Map<string, OracleIsJsonHit>();
